import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  PRIORITY_DEFS,
  type AgencyFieldValue,
  type AgencyFieldView,
  type AgencyProjectDetail,
  type AgencyTaskView,
  type Priority,
  type RetainerUsageView,
} from '@goyone/shared';
import { api } from '../../lib/api';
import { date } from '../../lib/format';
import { Icon } from '../../components/Icon';
import { ErrorState, Modal } from '../../components/ui';
import { Avatar, OwnerPickerModal, useTeamDirectory } from '../../components/People';
import { Feedback } from '../../components/Feedback';
import { Attachments } from '../../components/Attachments';
import { EntityRating } from '../../components/RatingRequest';
import { useToast } from '../../components/Toast';
import { useAuth, useSession } from '../../auth/AuthProvider';
import { ClientPreviewModal } from './Previews';

/**
 * بطاقة المهمة — بنمط لوحة أسانا الجانبية.
 *
 * صفوفٌ هادئة: التسمية يمينًا والقيمة أمامها، لا شبكة حقول مزدحمة.
 * والمهمة الفرعية تُفتح في البطاقة نفسها وتتفرّع بدورها — سلسلةٌ تصعد
 * بزرّ «المهمة الأم» ولا تضيع في نوافذ متراكبة.
 *
 * ## من يفعل ماذا
 *
 * الموظف ينفّذ: يُكمل ويعلّق ويرفع الملفات ويفرّع مهمته لزملائه.
 * والمدير يخطّط: يحذف والمهام الرئيسية والحقول له. الأزرار تُخفى عمّن
 * لا يملكها والخادم يحرسها ثانيةً — الإخفاء أدبٌ والحراسة أمن.
 */

type TaskDetail = AgencyTaskView & { subtasks: AgencyTaskView[] };

export function TaskDrawer({ taskId, onClose }: { taskId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const toast = useToast();
  const session = useSession();
  const { atLeast } = useAuth();
  const { byId, assignable } = useTeamDirectory();

  /* سلسلة التفرّع: فتح فرعيةٍ يدفعها للسلسلة، و«المهمة الأم» يرجع خطوة. */
  /*
   * دربُ العودة إلى ملف العميل.
   *
   * الدرج يُفتح من ملف العميل ومن لوح المشروع سواء. من جاء من الملف
   * يجب أن يعود إليه بعد أن يذهب إلى المشروع — فيُحمَل مسارُه في
   * الرابط (`backTo`) ويعرضه اللوح زرَّ عودةٍ صريحًا.
   */
  const { pathname } = useLocation();
  const backToClient =
    pathname.startsWith('/crm/leads/') || pathname.startsWith('/crm/accounts/')
      ? pathname
      : null;

  /* إعادة فتح المنجَزة — بكلمة سرّ الحماية، والخادم هو الحكم. */
  const [reopening, setReopening] = useState(false);
  const [reopenPass, setReopenPass] = useState('');
  const [reopenErr, setReopenErr] = useState<string | null>(null);
  const [stack, setStack] = useState<string[]>([taskId]);
  const currentId = stack[stack.length - 1]!;

  const [subtaskTitle, setSubtaskTitle] = useState('');
  const [notesDraft, setNotesDraft] = useState<string | null>(null);
  const [titleDraft, setTitleDraft] = useState<string | null>(null);
  const [unitsDraft, setUnitsDraft] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [clientPreview, setClientPreview] = useState(false);
  const panelRef = useRef<HTMLElement>(null);

  const detail = useQuery({
    queryKey: ['agency', 'task', currentId],
    queryFn: () => api<TaskDetail>(`/agency/tasks/${currentId}`),
  });

  const services = useQuery({
    queryKey: ['agency', 'catalog-services'],
    queryFn: () =>
      api<{ items: { id: string; name: string; unit: string }[] }>(
        '/sales/services?pageSize=100&isActive=true',
      ),
    staleTime: 5 * 60_000,
  });

  const retainer = useQuery({
    queryKey: ['agency', 'retainer', detail.data?.accountId],
    queryFn: () => api<RetainerUsageView | null>(`/agency/retainers/${detail.data!.accountId}`),
    enabled: Boolean(detail.data?.accountId),
  });

  const project = useQuery({
    queryKey: ['agency', 'project', detail.data?.projectId],
    queryFn: () => api<AgencyProjectDetail>(`/agency/projects/${detail.data!.projectId}`),
    enabled: Boolean(detail.data?.projectId),
  });

  const refresh = () => void queryClient.invalidateQueries({ queryKey: ['agency'] });

  const update = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/agency/tasks/${currentId}`, { method: 'PATCH', body }),
    onSuccess: refresh,
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const reopen = useMutation({
    mutationFn: () =>
      api(`/agency/tasks/${currentId}/reopen`, {
        method: 'POST',
        body: { password: reopenPass },
      }),
    onSuccess: () => {
      setReopening(false);
      setReopenPass('');
      setReopenErr(null);
      toast('أُعيد فتح المهمة');
      refresh();
      /* نشاط الملف عاد معها — فيُحدَّث سجلّ التعامل كذلك. */
      void queryClient.invalidateQueries({ queryKey: ['activities'] });
    },
    onError: (e) => setReopenErr(e instanceof Error ? e.message : 'تعذّرت إعادة الفتح.'),
  });

  const updateOther = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api(`/agency/tasks/${id}`, { method: 'PATCH', body }),
    onSuccess: refresh,
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ.'),
  });

  const addSubtask = useMutation({
    mutationFn: (title: string) =>
      api(`/agency/tasks`, {
        method: 'POST',
        body: { projectId: detail.data!.projectId, parentId: currentId, title },
      }),
    onSuccess: () => {
      setSubtaskTitle('');
      refresh();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّرت الإضافة.'),
  });

  /* حذف المنجَزة خلف كلمة سرّ الحذف — النافذة تجمعها والخادم يحكم. */
  const [deleteAsk, setDeleteAsk] = useState(false);
  const [deletePass, setDeletePass] = useState('');

  const remove = useMutation({
    mutationFn: (password?: string) =>
      api(`/agency/tasks/${currentId}`, {
        method: 'DELETE',
        ...(password ? { body: { password } } : {}),
      }),
    onSuccess: () => {
      setDeleteAsk(false);
      refresh();
      toast('حُذفت المهمة');
      if (stack.length > 1) setStack((s) => s.slice(0, -1));
      else onClose();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحذف.'),
  });

  useEffect(() => {
    setNotesDraft(null);
    setTitleDraft(null);
    setSubtaskTitle('');
    setUnitsDraft(null);
  }, [currentId]);

  /* Escape يغلق اللوحة — ما لم تكن نافذةٌ فرعية (كاختيار المسؤول) مفتوحة. */
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !picking && !clientPreview) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [picking, clientPreview, onClose]);

  /*
   * النقرة خارج اللوحة تغلقها — كأسانا، بلا رحلةٍ إلى زرّ الإغلاق.
   *
   * تُستثنى النوافذ والقوائم المرسومة خارج اللوحة (بوّابة React): اختيار
   * المسؤول والمعاينة وقائمة الإجراءات كلها «داخل» البطاقة منطقيًّا وإن
   * رُسمت خارجها شجريًّا.
   */
  useEffect(() => {
    const onPointerDown = (e: PointerEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (target.closest('.modal-scrim, .modal, [role="dialog"], .menu-panel, .toast')) return;
      onClose();
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [onClose]);

  const t = detail.data;
  const manager = atLeast('MANAGER');
  /* الموظف يفرّع مهمته هو — القاعدة نفسها التي يفرضها الخادم. */
  const canAddSubtask = manager || t?.assigneeId === session.user.id;
  const assignee = t?.assigneeId ? byId.get(t.assigneeId) : null;
  const packageItem =
    t?.serviceId && retainer.data
      ? (retainer.data.items.find((i) => i.serviceId === t.serviceId) ?? null)
      : null;

  return (
    <aside className="ag-panel" role="dialog" aria-label="تفاصيل المهمة" ref={panelRef}>
      {detail.isPending && <div className="skeleton" style={{ height: 200 }} />}
      {detail.isError && <ErrorState error={detail.error} onRetry={() => void detail.refetch()} />}

      {t && (
        <div className="ag-drawer">
          {/* شريط الرأس: إكمال + رجوع للأم + إغلاق */}
          <div className="ag-drawer-bar">
            {/*
              المنجَزة مجمَّدة، والفتحُ بابُه كلمة سرّ الحماية.
              كانت شهادةً لا رجعة فيها: من علّم مهمةً منجزةً خطأً يتركها
              كاذبةً في اللوح ويُنشئ نسخةً ثانية. الآن تُنقر فتُسأل.
            */}
            <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
              <button
                className={`ag-complete-btn${t.completed ? ' is-done' : ''}`}
                title={
                  t.completed
                    ? 'منجَزةٌ ومجمَّدة — انقر لإعادة فتحها بكلمة سرّ الحماية'
                    : undefined
                }
                onClick={() =>
                  t.completed ? setReopening(true) : update.mutate({ completed: true })
                }
              >
                <Icon name={t.completed ? 'refresh' : 'check'} size={13} />
                {t.completed ? 'مكتملة — مجمَّدة' : 'تحديد كمكتملة'}
              </button>

              {/* المشروع في متناول اليد: المهمة تُدار في لوحها لا في هذا الدرج وحده. */}
              <Link
                className="btn btn-sm"
                to={`/agency/projects/${t.projectId}?task=${t.id}${
                  backToClient ? `&backTo=${encodeURIComponent(backToClient)}` : ''
                }`}
                title="افتح المهمة في لوح مشروعها"
              >
                <Icon name="rocket" size={13} />
                الذهاب إلى المشروع
              </Link>
            </span>
            <span className="row" style={{ gap: 4 }}>
              {t.parentId && (
                <button
                  className="btn btn-sm btn-ghost"
                  onClick={() =>
                    setStack((s) => (s.length > 1 ? s.slice(0, -1) : [t.parentId!]))
                  }
                >
                  <Icon name="chevronUp" size={13} />
                  المهمة الأم
                </button>
              )}
              {/*
                إغلاقٌ مقصود بزرّه — إلى جانب النقر خارج اللوح الذي يبقى:
                النقرة الخارجية قد تصادف صفَّ مهمةٍ أخرى فتفتحها، وهذا
                الزرّ يغلق بلا مفاجآت. والمسودّات المعلّقة تُلتزم هنا
                صراحةً — لا اعتمادًا على أن نقرة الزرّ ستُغادر الحقل.
              */}
              <button
                className="btn btn-sm"
                onClick={() => {
                  (document.activeElement as HTMLElement | null)?.blur();
                  const tv = (titleDraft ?? '').trim();
                  if (titleDraft !== null && tv && tv !== t.title) update.mutate({ title: tv });
                  if (notesDraft !== null && notesDraft !== (t.notes ?? '')) {
                    update.mutate({ notes: notesDraft });
                  }
                  const uv = Number(unitsDraft);
                  if (unitsDraft !== null && uv > 0 && uv !== t.units) update.mutate({ units: uv });
                  setTimeout(onClose, 0);
                }}
              >
                <Icon name="check" size={13} />
                حفظ وإغلاق
              </button>
              <button
                className="btn btn-sm btn-icon btn-ghost"
                onClick={onClose}
                aria-label="إغلاق"
              >
                <Icon name="x" size={16} />
              </button>
            </span>
          </div>

          {/* العنوان */}
          <div className="ag-drawer-title">
            {t.isMilestone && <Icon name="diamond" size={16} className="ag-milestone-ic" />}
            <input
              className="ag-title-input"
              value={titleDraft ?? t.title}
              readOnly={t.completed}
              onChange={(e) => setTitleDraft(e.target.value)}
              onBlur={() => {
                const v = (titleDraft ?? '').trim();
                if (titleDraft !== null && v && v !== t.title) update.mutate({ title: v });
                setTitleDraft(null);
              }}
            />
          </div>
          {t.completed && t.completedAt && (
            <p className="tiny mute">اكتملت {date(t.completedAt)}</p>
          )}

          {/* صفوف الحقول — تسمية وقيمة، كأسانا */}
          <div className="ag-frows">
            <FieldRow label="المسؤول">
              <button className="ag-assignee-btn" onClick={() => setPicking(true)}>
                {assignee ? (
                  <>
                    <Avatar name={assignee.name} src={assignee.avatarUrl} size={24} />
                    <span>{assignee.name}</span>
                  </>
                ) : (
                  <>
                    <span className="ag-assignee-empty">
                      <Icon name="user" size={13} />
                    </span>
                    <span className="mute">بلا مسؤول</span>
                  </>
                )}
              </button>
            </FieldRow>

            <FieldRow label="المدة">
              <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                <input
                  type="date"
                  className="ag-inline-input"
                  value={t.startAt ? t.startAt.slice(0, 10) : ''}
                  onChange={(e) =>
                    update.mutate({ startAt: e.target.value ? e.target.value : null })
                  }
                  title="تاريخ البدء"
                  aria-label="تاريخ البدء"
                />
                <span className="mute">←</span>
                <input
                  type="date"
                  className="ag-inline-input"
                  value={t.dueAt ? t.dueAt.slice(0, 10) : ''}
                  onChange={(e) => update.mutate({ dueAt: e.target.value ? e.target.value : null })}
                  title="تاريخ الاستحقاق"
                  aria-label="تاريخ الاستحقاق"
                />
              </span>
            </FieldRow>

            <FieldRow label="المشروع">
              <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                <Link to={`/agency/projects/${t.projectId}`} className="ag-ctx-chip">
                  <span className="ag-dot" style={{ background: t.projectColor ?? undefined }} />
                  {t.projectName ?? 'المشروع'}
                </Link>
                {!t.parentId && (project.data?.sections.length ?? 0) > 0 && (
                  <select
                    className="ag-inline-input"
                    value={t.sectionId ?? ''}
                    onChange={(e) => e.target.value && update.mutate({ sectionId: e.target.value })}
                  >
                    {!t.sectionId && <option value="">بلا قسم</option>}
                    {project.data!.sections.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                )}
              </span>
            </FieldRow>

            {t.accountId && (
              <FieldRow label="العميل">
                {/* معاينةٌ للقراءة لا ملفُّ التعديل — المنفّذ يحتاج السياق لا الأبواب. */}
                <button className="ag-ctx-chip" onClick={() => setClientPreview(true)}>
                  <Icon name="building" size={13} />
                  {t.accountName ?? 'العميل'}
                </button>
              </FieldRow>
            )}

            <FieldRow label="الأولوية">
              <select
                className="ag-inline-input"
                value={t.priority}
                onChange={(e) => update.mutate({ priority: e.target.value as Priority })}
              >
                {PRIORITY_DEFS.map((p) => (
                  <option key={p.key} value={p.key}>
                    {p.label}
                  </option>
                ))}
              </select>
            </FieldRow>

            {!t.parentId && (
              <FieldRow label="النوع">
                <label className="row small" style={{ gap: 6, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={t.isMilestone}
                    onChange={(e) => update.mutate({ isMilestone: e.target.checked })}
                  />
                  <Icon name="diamond" size={13} className="ag-milestone-ic" />
                  معلمٌ رئيسيّ
                </label>
              </FieldRow>
            )}

            <FieldRow label="الخدمة">
              <span className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                <select
                  className="ag-inline-input"
                  value={t.serviceId ?? ''}
                  onChange={(e) => update.mutate({ serviceId: e.target.value || null })}
                >
                  <option value="">بلا خدمة</option>
                  {(services.data?.items ?? []).map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
                {t.serviceId && (
                  <input
                    type="number"
                    min={0.5}
                    step={0.5}
                    className="ag-inline-input num"
                    style={{ width: '5.5rem' }}
                    value={unitsDraft ?? String(t.units)}
                    onChange={(e) => setUnitsDraft(e.target.value)}
                    onBlur={() => {
                      const v = Number(unitsDraft);
                      if (unitsDraft !== null && v > 0 && v !== t.units) update.mutate({ units: v });
                      setUnitsDraft(null);
                    }}
                    aria-label="الوحدات"
                  />
                )}
              </span>
            </FieldRow>

            {/* حقول المشروع المخصَّصة */}
            {(project.data?.fieldDefs ?? []).map((f) => (
              <FieldRow key={`${t.id}:${f.id}`} label={f.name}>
                <FieldControl
                  field={f}
                  value={t.fields[f.id] ?? null}
                  members={assignable}
                  onSave={(v) => update.mutate({ fields: { ...t.fields, [f.id]: v } })}
                />
              </FieldRow>
            ))}
          </div>

          {t.serviceId && t.accountId && retainer.data && (
            <p
              className={`tiny ${packageItem && packageItem.remaining < t.units && !t.completed ? 'ag-late-text' : 'mute'}`}
            >
              {packageItem
                ? `باقة «${retainer.data.packageName}»: متبقٍ ${packageItem.remaining} من ${packageItem.allowance} ${packageItem.unit} هذه الدورة${
                    packageItem.over > 0 ? ` — تجاوزٌ بمقدار ${packageItem.over}` : ''
                  }`
                : `هذه الخدمة خارج باقة «${retainer.data.packageName}» — تُحصى عملًا إضافيًّا.`}
            </p>
          )}

          {/* الوصف */}
          <div className="ag-sec">
            <h4 className="label">الوصف</h4>
            <textarea
              className="textarea"
              rows={3}
              value={notesDraft ?? t.notes ?? ''}
              onChange={(e) => setNotesDraft(e.target.value)}
              onBlur={() => {
                if (notesDraft !== null && notesDraft !== (t.notes ?? '')) {
                  update.mutate({ notes: notesDraft });
                }
                setNotesDraft(null);
              }}
              placeholder="ما المطلوب بالضبط، وما يلزم لإنجازه…"
            />
          </div>

          {/* المرفقات — تصاميم وملفات عمل على المهمة نفسها */}
          <Attachments entity="task" entityId={t.id} title="ملفات المهمة" />

          {/*
            رأي العميل في هذه المهمة بعينها — رئيسيةً كانت أو فرعية.

            ولكلِّ مهمّةٍ لا لذواتِ العميلِ وحدَها: التسليمُ يُقيَّم أيًّا
            كان بابُه، وإخفاءُ البابِ عن مهمّةٍ بلا عميلٍ مسجَّل كان يحرم
            عملًا حقيقيًّا من شهادةٍ عليه. ومن لا عميلَ له يُقال له ذلك
            صراحةً: الرأيُ يُحفظ ولا يبلغ ملفَّ أحد.
          */}
          <div className="ag-sec">
            <h4 className="label">تقييم العميل</h4>
            <EntityRating kind="TASK" entityId={t.id} />
            {/* والشرطُ ترتيبُ الخادمِ نفسُه: المهمّةُ ثمّ مشروعُها. */}
            {!t.accountId && project.isSuccess && !project.data.accountId && (
              <p className="tiny mute">
                لا عميلَ على هذه المهمة ولا على مشروعها — التقييمُ يُحفظ على المهمة ولا يظهر
                في ملفِّ عميل.
              </p>
            )}
          </div>

          {/* المهام الفرعية — تُفتح وتتفرّع بدورها */}
          <div className="ag-sec">
            <h4 className="label">
              المهام الفرعية{' '}
              {t.subtasks.length > 0 && (
                <span className="tiny mute num">
                  {t.subtasks.filter((s) => s.completed).length}/{t.subtasks.length}
                </span>
              )}
            </h4>
            {t.subtasks.map((s) => {
              const subAssignee = s.assigneeId ? byId.get(s.assigneeId) : null;
              return (
                <div key={s.id} className="ag-subtask">
                  <button
                    className={`ag-check ag-check-sm${s.completed ? ' is-done' : ''}`}
                    onClick={() => updateOther.mutate({ id: s.id, body: { completed: !s.completed } })}
                    aria-label={s.completed ? 'إعادة فتح' : 'إكمال'}
                  >
                    <Icon name="check" size={11} />
                  </button>
                  <button
                    className={`ag-subtask-open${s.completed ? ' ag-done-text' : ''}`}
                    onClick={() => setStack((st) => [...st, s.id])}
                    title="افتح المهمة الفرعية"
                  >
                    {s.title}
                  </button>
                  {s.subtaskCount > 0 && (
                    <span className="tiny mute num">
                      {s.subtaskDone}/{s.subtaskCount}
                    </span>
                  )}
                  {s.dueAt && <span className="tiny mute num">{date(s.dueAt)}</span>}
                  {subAssignee && (
                    <span title={subAssignee.name}>
                      <Avatar name={subAssignee.name} src={subAssignee.avatarUrl} size={20} />
                    </span>
                  )}
                </div>
              );
            })}

            {canAddSubtask ? (
              <input
                className="input"
                value={subtaskTitle}
                onChange={(e) => setSubtaskTitle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && subtaskTitle.trim()) {
                    addSubtask.mutate(subtaskTitle.trim());
                  }
                }}
                placeholder="أضف مهمة فرعية واضغط Enter"
              />
            ) : (
              t.subtasks.length === 0 && (
                <p className="tiny mute">التفريع لمسؤول المهمة أو الإدارة.</p>
              )
            )}
          </div>

          {/* التعليقات — كتابةً وصوتًا ومنشن */}
          <Feedback entity="task" entityId={t.id} title="التعليقات" />

          {manager && (
            <div className="row" style={{ justifyContent: 'flex-end' }}>
              {/* هادئٌ بمظهر الأزرار العادية — ويحمرّ عند المرور ليقول ما هو. */}
              <button
                className="btn btn-sm btn-danger-hover"
                onClick={() => {
                  /* المنجَزة تاريخٌ — بابها كلمة سرّ الحذف لا نقرة تأكيد. */
                  if (t.completed) {
                    setDeletePass('');
                    setDeleteAsk(true);
                    return;
                  }
                  if (window.confirm('حذف المهمة ومهامها الفرعية؟')) remove.mutate(undefined);
                }}
              >
                <Icon name="trash" size={14} />
                حذف المهمة
              </button>
            </div>
          )}

          {/* بوّابة إعادة الفتح — كلمة سرّ الحماية نفسها التي تحرس المحو. */}
          {reopening && (
            <Modal
              title="إعادة فتح المهمة"
              onClose={() => {
                setReopening(false);
                setReopenErr(null);
              }}
              footer={
                <>
                  <button className="btn" onClick={() => setReopening(false)}>
                    إلغاء
                  </button>
                  <button
                    className="btn btn-primary"
                    disabled={!reopenPass || reopen.isPending}
                    onClick={() => reopen.mutate()}
                  >
                    {reopen.isPending ? 'جارٍ الفتح' : 'أعد فتحها'}
                  </button>
                </>
              }
            >
              {reopenErr && <div className="alert alert-danger mb-4">{reopenErr}</div>}
              <p className="small mb-4">
                ستعود المهمة مفتوحةً ويُمحى تاريخ إنجازها — ونشاطُها في ملف العميل يعود
                معها. اكتب كلمة سرّ الحماية للمتابعة.
              </p>
              <div className="field">
                <label className="label" htmlFor="task-reopen-pass">
                  كلمة سرّ الحماية
                </label>
                <input
                  id="task-reopen-pass"
                  className="input"
                  type="password"
                  value={reopenPass}
                  onChange={(e) => setReopenPass(e.target.value)}
                  autoFocus
                />
              </div>
            </Modal>
          )}

          {clientPreview && t.accountId && (
            <ClientPreviewModal accountId={t.accountId} onClose={() => setClientPreview(false)} />
          )}

          {/* حارس حذف المنجَزة — المالكُ وكلمةُ سرّ الحذف من الإعدادات. */}
          {deleteAsk && (
            <Modal
              title="حذف مهمةٍ منجَزة"
              onClose={() => setDeleteAsk(false)}
              footer={
                <>
                  <button className="btn" onClick={() => setDeleteAsk(false)}>
                    إلغاء
                  </button>
                  <button
                    className="btn btn-danger"
                    disabled={!deletePass || remove.isPending}
                    onClick={() => remove.mutate(deletePass)}
                  >
                    {remove.isPending ? 'جارٍ الحذف…' : 'احذف نهائيًّا'}
                  </button>
                </>
              }
            >
              <p className="small mute mb-3">
                هذه مهمةٌ منجَزة — حذفها محوٌ لتاريخ عملٍ وقع، وهو للمالك وحده
                وبكلمة سرّ الحذف المضبوطة في «الإعدادات ← الحماية».
              </p>
              <label className="field">
                <span className="label">كلمة سرّ الحذف</span>
                <input
                  className="input"
                  type="password"
                  value={deletePass}
                  onChange={(e) => setDeletePass(e.target.value)}
                  autoFocus
                />
              </label>
            </Modal>
          )}

          {picking && (
            <OwnerPickerModal
              currentId={t.assigneeId}
              title="مسؤول المهمة"
              pending={update.isPending}
              error={null}
              onPick={(userId) => {
                update.mutate({ assigneeId: userId });
                setPicking(false);
              }}
              onClose={() => setPicking(false)}
            />
          )}
        </div>
      )}
    </aside>
  );
}

/** صفّ تسمية/قيمة — لبِنة الشكل الأساسية. */
function FieldRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="ag-frow">
      <span className="ag-frow-label">{label}</span>
      <span className="ag-frow-value">{children}</span>
    </div>
  );
}

/**
 * قيمة حقلٍ مخصَّص بحسب نوعه.
 */
function FieldControl({
  field,
  value,
  members,
  onSave,
}: {
  field: AgencyFieldView;
  value: AgencyFieldValue;
  members: { userId: string; name: string; avatarUrl: string | null }[];
  onSave: (v: AgencyFieldValue) => void;
}) {
  if (field.kind === 'SELECT') {
    const current = field.options.find((o) => o.id === value);
    return (
      <span className="row" style={{ gap: 8 }}>
        {current && <span className="ag-dot" style={{ background: current.color }} />}
        <select
          className="ag-inline-input"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onSave(e.target.value || null)}
        >
          <option value="">—</option>
          {field.options.map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
            </option>
          ))}
        </select>
      </span>
    );
  }

  if (field.kind === 'MULTI_SELECT') {
    const selected = Array.isArray(value) ? value : [];
    return (
      <span className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
        {field.options.map((o) => {
          const on = selected.includes(o.id);
          return (
            <button
              key={o.id}
              type="button"
              className="ag-chip ag-chip-toggle"
              style={
                on
                  ? { color: '#fff', background: o.color, borderColor: o.color }
                  : { color: o.color, borderColor: `${o.color}66`, background: `${o.color}12` }
              }
              onClick={() =>
                onSave(on ? selected.filter((x) => x !== o.id) : [...selected, o.id])
              }
              aria-pressed={on}
            >
              {o.label}
            </button>
          );
        })}
      </span>
    );
  }

  if (field.kind === 'DATE') {
    return (
      <input
        type="date"
        className="ag-inline-input"
        value={typeof value === 'string' ? value.slice(0, 10) : ''}
        onChange={(e) => onSave(e.target.value || null)}
      />
    );
  }

  if (field.kind === 'PEOPLE') {
    const member = typeof value === 'string' ? members.find((m) => m.userId === value) : null;
    return (
      <span className="row" style={{ gap: 8 }}>
        {member && <Avatar name={member.name} src={member.avatarUrl} size={20} />}
        <select
          className="ag-inline-input"
          value={typeof value === 'string' ? value : ''}
          onChange={(e) => onSave(e.target.value || null)}
        >
          <option value="">—</option>
          {members.map((m) => (
            <option key={m.userId} value={m.userId}>
              {m.name}
            </option>
          ))}
        </select>
      </span>
    );
  }

  if (field.kind === 'CHECKBOX') {
    return (
      <input
        type="checkbox"
        checked={value === true}
        onChange={(e) => onSave(e.target.checked ? true : null)}
      />
    );
  }

  return (
    <input
      type={field.kind === 'NUMBER' ? 'number' : 'text'}
      className={`ag-inline-input${field.kind === 'NUMBER' ? ' num' : ''}`}
      defaultValue={typeof value === 'string' || typeof value === 'number' ? value : ''}
      onBlur={(e) => {
        const raw = e.target.value.trim();
        if (raw === '' && value == null) return;
        const next = raw === '' ? null : field.kind === 'NUMBER' ? Number(raw) : raw;
        if (next !== value && !(field.kind === 'NUMBER' && Number.isNaN(next))) onSave(next);
      }}
    />
  );
}
